import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/* The Xano signatures these stand in for were untyped, and the screens read
   fields off them freely. Narrowing here would only move the errors. */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * The quotation screens, off Xano.
 *
 * These keep the shapes the Xano service handed back so the screens were an
 * import change rather than a rewrite. Where Xano took a
 * bundle of figures the browser had already worked out and printed them
 * straight onto the document, these send the *inputs* instead and let the
 * API price it — which is the point of the move, not a side effect of it.
 * A quotation can no longer disagree with the deal it turns into.
 */

type Payload<T> = AxiosRequestConfig<T>;

/** What both PDF endpoints hand back. */
type QuotationResult = { url: string; emailed?: unknown };

/** Xano's forex product ids; 16 is the travel card, 9 the notes. */
const FOREX_CARD_PRODUCT_ID = 16;

const text = (value: unknown): string | undefined => {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? undefined : trimmed;
};

/**
 * A figure the screen formatted for display, back as a number.
 *
 * These payloads carry things like `"₹ 1,23,456.78"` because Xano's template
 * printed them verbatim. The API wants the number.
 */
const amount = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const digits = String(value ?? "").replace(/[^0-9.-]/g, "");
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : 0;
};

/* --------------------------------------------------------- who is signed in */

export type GetInfoRequestType = Partial<{
  headers: { Authorization: string };
}>;

/**
 * The signed-in member of staff.
 *
 * Xano returned the whole `staff` row with `name` copied off `fname`; the
 * screens read `name` for the sign-off on the quotation and nothing else.
 * `/auth/me` is the same person, so the row is that reshaped rather than a
 * second call to something staff-shaped.
 */
export async function getInfo(
  _payload: Payload<GetInfoRequestType>,
): Promise<{ data: any }> {
  const { data } = await moneefloApi.get("/auth/me");
  const me = data as {
    id: string;
    fullName: string;
    email: string;
    organization: { id: string; name: string } | null;
  };

  return {
    data: {
      id: me.id,
      name: me.fullName,
      fname: me.fullName,
      email: me.email,
      channels_id: me.organization?.id ?? null,
      channel_name: me.organization?.name ?? null,
    },
  };
}

/* ------------------------------------------------------------ one partner */

export type GetPartnerInfoRequestType = Partial<{
  headers: { Authorization: string };
  params: { channels_id: number | string };
}>;

/**
 * A partner channel by id, for the name printed on an L2 quotation.
 *
 * Xano's `channels` row is an organization here. Only `name` is read, but
 * the address fields come across too: the remittance quotation screens put
 * the city on the document.
 */
export async function getPartnerInfo(
  payload: Payload<GetPartnerInfoRequestType>,
): Promise<{ data: any }> {
  const id = text(payload.params?.channels_id);
  if (!id) return { data: null };

  const { data } = await moneefloApi.get(
    `/organizations/${encodeURIComponent(id)}`,
  );
  const organization = data as {
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
    email?: string | null;
    phone?: string | null;
  };

  return {
    data: {
      id: organization.id,
      name: organization.name,
      channel_name: organization.name,
      city: organization.city ?? "",
      state: organization.state ?? "",
      email: organization.email ?? "",
      phone_number: organization.phone ?? "",
    },
  };
}

/* ------------------------------------------------------ GST on a conversion */

export type GetGSTRequestType = Partial<{
  headers: { Authorization: string };
  params: { amount: number | string };
}>;

/**
 * GST on a counter conversion, Rule 32(2)(b), band by band.
 *
 * The screens show the bands as well as the total, which is why this is an
 * endpoint and not a line of arithmetic in the browser — the bands are the
 * same ones the order is charged on, and they have to agree.
 */
export async function getGST(
  payload: Payload<GetGSTRequestType>,
): Promise<{ data: any }> {
  const { data } = await moneefloApi.get("/pricing/forex-gst", {
    params: { amount: amount(payload.params?.amount) },
  });
  return { data };
}

/* ------------------------------------------------- the counter's quotation */

export type GetPDFURLRequestType = Partial<{
  headers: { Authorization: string };
  data: Record<string, any>;
}>;

/**
 * The forex quotation, priced and drawn by the API.
 *
 * Xano was handed the finished document — every total the browser had
 * computed, already formatted — and printed it. This sends the basket, what
 * was typed for each rate and the service charge, and nothing else: a line
 * with no rate on it is priced off the live feed and the held spread, and
 * the GST on the conversion is worked out server-side under Rule 32(2)(b).
 *
 * Returns the url as `data`, because that is what the screen downloads.
 */
export async function getPDFURL(
  payload: Payload<GetPDFURLRequestType>,
): Promise<{ data: string }> {
  const body = payload.data ?? {};
  const sheet = (body.data ?? {}) as Record<string, any>;
  const lines = (body.products ?? []) as Record<string, any>[];

  if (lines.length === 0) {
    throw new Error("Add at least one product before quoting.");
  }

  // "encashment" is the customer selling notes back to us; anything else is
  // them buying.
  const action = String(body.action ?? sheet.product_type ?? "").toLowerCase();
  const direction = action === "encashment" ? "sell" : "buy";

  const products = lines.map((line) => {
    const currency = text(line.product_currency);
    if (!currency || currency === "INR") {
      throw new Error("Every product needs a currency before quoting.");
    }
    const rate = amount(line.product_rate);
    return {
      currency,
      fxProduct:
        Number(line.product_id) === FOREX_CARD_PRODUCT_ID ? "card" : "currency",
      productName: text(line.product_name),
      quantity: amount(line.product_quantity),
      // Left out when the operator did not overrule it, which is the signal
      // to price the line off the feed.
      rate: rate > 0 ? rate : undefined,
    };
  });

  // Each line carries its charge already grossed up for its own GST, so the
  // basket's charge is their sum and it is inclusive. Sending it exclusive
  // would have the API add 18% to a figure that already had it.
  const serviceCharge = lines.reduce(
    (total, line) => total + amount(line.product_service_charge),
    0,
  );

  const { data } = await moneefloApi.post("/quotations/forex-pdf", {
    direction,
    products,
    serviceCharge,
    serviceChargeIncludesGst: true,
    customerName: text(body.customer_name ?? sheet.customer_name),
    customerEmail: text(body.email ?? sheet.customer_email),
    customerPhone: text(body.phone_number ?? sheet.customer_phone_number),
    passportNumber: text(body.passport ?? sheet.customer_passport),
    // Quoting somebody at the counter starts a lead, as it did on Xano.
    openLead: true,
  });

  return { data: (data as QuotationResult).url };
}

/* ----------------------------------------------- the remittance quotation */

export type GetRemittancePDFURLRequestType = Partial<{
  headers: { Authorization: string };
  data: Record<string, any>;
}>;

/**
 * The remittance quotation, priced and drawn by the API.
 *
 * As above: Xano took the formatted totals and printed them, so the payload
 * is full of strings like `"₹ 1,23,456.78"`. Only the inputs go over — the
 * purpose, the currency, the amount and the rate the operator promised —
 * and the charges and TCS come back off the same calculation the order will
 * use.
 */
export async function getRemittancePDFURL(
  payload: Payload<GetRemittancePDFURLRequestType>,
): Promise<{ data: string }> {
  const body = payload.data ?? {};
  const sheet = (body.data ?? {}) as Record<string, any>;

  const purposeId = text(sheet.purpose ?? body.purpose_id);
  const currency = text(sheet.currency)?.toUpperCase();
  // `beneficiary_received` reads "USD 1,234.5600"; the units are what matter.
  const fxAmount = amount(
    sheet.fx_amount ?? String(sheet.beneficiary_received ?? "").replace(/^\D+/, ""),
  );

  if (!purposeId || !currency || fxAmount <= 0) {
    throw new Error(
      "A purpose, a currency and an amount are needed to quote.",
    );
  }

  const customerRate = amount(sheet.customer_rate);
  const ttCharge = amount(sheet.tt_charges);
  const foreignBankCharge = amount(sheet.fb_charges);

  const { data } = await moneefloApi.post("/quotations/pdf", {
    purposeId,
    currency,
    fxAmount,
    customerRate: customerRate > 0 ? customerRate : undefined,
    // Zero is a real answer here — a corridor with no TT charge — so these
    // only fall back to the configured figure when the screen sent nothing.
    ttCharge: sheet.tt_charges === undefined ? undefined : ttCharge,
    foreignBankCharge:
      sheet.fb_charges === undefined ? undefined : foreignBankCharge,
    customerName: text(sheet.customer_name),
    customerPhone: text(sheet.customer_number),
    country: text(sheet.country),
    openLead: true,
  });

  return { data: (data as QuotationResult).url };
}

/* ------------------------------------------ asking the desk above for a price */

export type RequestQuotationL2ToL1RequestType = Partial<{
  headers: { Authorization: string };
  data: { opportunity_id: number | string };
  opportunity_id: number | string;
}>;

/**
 * An L2 partner sending a lead up to be priced.
 *
 * Xano set three fields on the opportunity and this sets the same three,
 * now that they exist on the lead here. Worth knowing why they were added
 * rather than mapped onto something: the read half of this had already
 * moved to the new backend, which carried no such flag, so every migrated
 * lead list has been showing "no request outstanding" regardless. Writing
 * to Xano was keeping a record nothing on this side could see.
 *
 * The screens only check the status, so that is what matters in the reply.
 */
export async function requestQuotationL2ToL1(
  payload: Payload<RequestQuotationL2ToL1RequestType>,
): Promise<{ status: number; data: any }> {
  const id = text(payload.data?.opportunity_id ?? payload.opportunity_id);
  if (!id) throw new Error("A lead is needed to request a quotation.");

  const { data } = await moneefloApi.post(
    `/opportunities/${encodeURIComponent(id)}/quotation-request`,
    { requested: true },
  );
  return { status: 200, data };
}

/* ----------------------------------------------------- notes on a contact */

export type GetPrepareQuoteNotesRequestType = Partial<{
  headers: { Authorization: string };
  params: { phone_number: string };
}>;

type CustomerRow = {
  id: string;
  fullName: string;
  phone: string | null;
  notes?: string | null;
};

/**
 * The customer behind a phone number, or nothing if the book has no one.
 *
 * Two calls rather than one: the list does not carry `notes`, so the row it
 * finds has to be fetched in full before the note can be read off it.
 */
async function customerByPhone(phone: string): Promise<CustomerRow | null> {
  const search = phone.trim();
  // Anything shorter matches half the book, and the box only fires at ten
  // digits anyway.
  if (search.length < 6) return null;

  const { data } = await moneefloApi.get("/customers", {
    params: { search, perPage: 10 },
  });
  const rows = (data?.items ?? []) as CustomerRow[];
  // A search matches on name and email too, so take the one whose number
  // this actually is rather than whoever came back first.
  const digits = search.replace(/\D/g, "");
  const match = rows.find((row) =>
    (row.phone ?? "").replace(/\D/g, "").endsWith(digits),
  );
  if (!match) return null;

  const { data: full } = await moneefloApi.get(
    `/customers/${encodeURIComponent(match.id)}`,
  );
  return { ...match, ...(full as CustomerRow) };
}

/**
 * What the desk has written against a contact.
 *
 * Xano kept these on its `contacts` row; they are the customer's `notes`
 * here. The shape is Xano's, down to the `_staff` the screens read for who
 * touched it last.
 */
export async function getPrepareQuoteNotes(
  payload: Payload<GetPrepareQuoteNotesRequestType>,
): Promise<{ data: any }> {
  const customer = await customerByPhone(payload.params?.phone_number ?? "");
  if (!customer) return { data: null };

  return {
    data: {
      id: customer.id,
      contactName: customer.fullName,
      contactNumber: customer.phone ?? "",
      notes: customer.notes ?? "",
      _staff: null,
    },
  };
}

export type PutUpdatePrepareQuoteNotesReqType = Partial<{
  headers: { Authorization: string };
  phone_number: string;
  notes: string;
  data: { phone_number?: string; notes?: string };
}>;

/**
 * Writes the note back.
 *
 * Xano looked the contact up by number and edited it in one call. Here it is
 * a lookup and a patch, so a number with nobody behind it is a no-op rather
 * than a silent create — the same thing Xano did, which edited nothing when
 * the contact was missing.
 */
export async function PutUpdatePrepareQuoteNotes(
  payload: Payload<PutUpdatePrepareQuoteNotesReqType>,
): Promise<{ data: any }> {
  const body = payload.data ?? {};
  const phone = (body.phone_number ?? payload.phone_number ?? "") as string;
  const notes = (body.notes ?? payload.notes ?? "") as string;

  const customer = await customerByPhone(phone);
  if (!customer) return { data: null };

  const { data } = await moneefloApi.patch(
    `/customers/${encodeURIComponent(customer.id)}`,
    { notes },
  );
  const updated = data as CustomerRow;

  return {
    data: {
      id: updated.id ?? customer.id,
      contactName: updated.fullName ?? customer.fullName,
      contactNumber: updated.phone ?? customer.phone ?? "",
      notes: updated.notes ?? notes,
      _staff: null,
    },
  };
}
